import axios from "axios";
import config from "./config";

/**
 * The affiliate section of a member's profile.
 *
 * ── Why these calls go to the membership admin app ──────────────────────────
 * The links, and the figures behind them, are kept by the platform the
 * creator sells through, whose bridge authenticates the CREATOR with a signed
 * JWT. That secret cannot be in a browser bundle — anyone holding it could act
 * as any creator. So this asks the admin app (MEMBERSHIP_API_URL), which
 * proves WHICH MEMBER is calling from the encrypted `member_token` and asks on
 * their behalf.
 *
 * Nothing here sends an email address. The member's identity comes out of the
 * database on the server — an email taken from the client would let any member
 * share, and collect, as any other.
 *
 * None of this is ever named to the member. They share a link and watch their
 * earnings; where the money is tracked is not their problem to understand.
 */

/**
 * What this member can share, and whether there is more to sign up for.
 *
 * @returns {Promise<{
 *   can_opt_in: boolean,
 *   has_affiliate_account?: boolean,
 *   promotable_products: Array<{product_id: number, product_title: string}>,
 *   links: Array<{product_id: number, product_title: string, link: string}>,
 *   reason?: string,
 * }>}
 */
export async function fetchAffiliateLinks({ token, memberToken }) {
  const res = await axios.post(
    `${config.MEMBERSHIP_API_URL}/membership-affiliate-links`,
    { membership_token: token, member_token: memberToken },
  );

  const data = res.data || {};

  // Normalised so the caller never has to tell an unreachable server from a
  // member with nothing to promote: both mean "show nothing".
  return {
    can_opt_in: Boolean(data.can_opt_in),
    has_affiliate_account: Boolean(data.has_affiliate_account),
    promotable_products: Array.isArray(data.promotable_products)
      ? data.promotable_products
      : [],
    links: Array.isArray(data.links) ? data.links : [],
    reason: data.reason || "",
  };
}

/**
 * Start sharing: one press, nothing asked for.
 *
 * No password, because the member is not signing up for anything they have to
 * manage. The server does whatever it needs to track what they earn, and the
 * member sees their links and their earnings here — which is the only place
 * they know about.
 */
export async function optInToAffiliate({ token, memberToken }) {
  const res = await axios.post(
    `${config.MEMBERSHIP_API_URL}/membership-affiliate-opt-in`,
    { membership_token: token, member_token: memberToken },
  );

  return res.data || {};
}

/**
 * What this member has earned from the links they share.
 *
 * Separate from the links call because it is a different question asked at a
 * different time: the links are needed to draw the panel at all, the earnings
 * only once there is something to have earned.
 *
 * @returns {Promise<{
 *   summary: {sales: number, products: number, earned: number, paid: number, pending: number},
 *   products: Array<{product_id: number, product_title: string, sales: number, earned: number}>,
 *   recent: Array<{date: string, product_title: string, earned: number, paid: boolean}>,
 *   currency: string,
 *   currencies: string[],
 * }>}
 */
export async function fetchAffiliateStats({ token, memberToken, currency }) {
  const res = await axios.post(
    `${config.MEMBERSHIP_API_URL}/membership-affiliate-stats`,
    { membership_token: token, member_token: memberToken, currency },
  );

  const data = res.data || {};
  const summary = data.summary || {};

  // Normalised so the caller never has to tell an unreachable server from a
  // member who has sold nothing: both mean "no figures to show".
  return {
    currency: data.currency || "",
    currencies: Array.isArray(data.currencies) ? data.currencies : [],
    summary: {
      sales: Number(summary.sales || 0),
      products: Number(summary.products || 0),
      earned: Number(summary.earned || 0),
      paid: Number(summary.paid || 0),
      pending: Number(summary.pending || 0),
    },
    products: Array.isArray(data.products) ? data.products : [],
    recent: Array.isArray(data.recent) ? data.recent : [],
  };
}
